import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Flame, Sparkles, Target, Clock } from 'lucide-react';
import { fetchPersonalStats } from '../../lib/api';
import Tooltip from './Tooltip';

/**
 * StreakWidget — mini-dashboard sidebar con 3 metriche:
 *   1. 🔥 Streak — settimane consecutive con ≥1 MIT chiusa
 *   2. 🎯 KR%   — percentuale di KR attivi on-track (confidence ≥ 0.7)
 *   3. ⏰ Coach — giorni dall'ultima review settimanale con il coach
 *
 * Colore per metrica:
 *   - Streak: emerald se ≥1, ambra se 0
 *   - KR%:    emerald ≥70, ambra 40-69, red <40, grigio se nessun KR
 *   - Coach:  emerald 0-2g, ambra 3-6g, red ≥7g, grigio se mai
 *
 * Layout:
 *   - Expanded: 1 riga "🔥 3 · 🎯 60% · ⏰ 5g" (con separatori)
 *   - Collapsed: 3 righe stack verticali con solo icona + numero
 *
 * Sfrutta cache TTL 60s di fetchPersonalStats. Click → /stats.
 * Hidden se nessun KR attivo o errore API.
 */
export default function StreakWidget({ collapsed = false }) {
  const [stats, setStats] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let mounted = true;
    fetchPersonalStats()
      .then((s) => { if (mounted) setStats(s); })
      .catch(() => { if (mounted) setHidden(true); });
    return () => { mounted = false; };
  }, []);

  if (hidden || !stats) return null;
  const streak = stats.streak_weeks || 0;
  const krActive = stats.kr?.total_active || 0;
  const krPct = stats.kr?.on_track_pct;
  const coachDays = stats.coach?.days_since_weekly_checkin; // null se mai
  if (krActive === 0) return null;

  const streakOn = streak >= 1;
  const StreakIcon = streakOn ? Flame : Sparkles;
  const streakColor = streakOn ? 'text-ok' : 'text-amber-700';

  const krColor =
    krPct == null ? 'text-mute'
      : krPct >= 70 ? 'text-ok'
      : krPct >= 40 ? 'text-amber-700'
      : 'text-risk';

  const coachColor =
    coachDays == null ? 'text-mute'
      : coachDays >= 7 ? 'text-risk'
      : coachDays >= 3 ? 'text-amber-700'
      : 'text-ok';
  const coachLabel = coachDays == null ? '—' : `${coachDays}g`;
  const coachTooltipPart =
    coachDays == null
      ? 'nessuna review settimanale ancora'
      : coachDays === 0 ? 'review settimanale di oggi'
      : `ultima review settimanale ${coachDays} ${coachDays === 1 ? 'giorno' : 'giorni'} fa`;

  const tooltip =
    `Streak ${streak} settiman${streak === 1 ? 'a' : 'e'} · ` +
    `${krPct ?? '—'}% KR on-track (${stats.kr?.on_track ?? 0}/${krActive}) · ` +
    coachTooltipPart;

  if (collapsed) {
    return (
      <Tooltip label={tooltip}>
        <Link
          to="/stats"
          className="group w-full flex flex-col items-center gap-1 px-3 py-2 hover:bg-linesoft transition-colors"
          data-testid="streak-widget"
          data-streak={streak}
          data-kr-pct={krPct ?? ''}
          data-coach-days={coachDays ?? ''}
          aria-label={tooltip}
        >
          <span className={`inline-flex items-center gap-1 ${streakColor}`}>
            <StreakIcon size={14} strokeWidth={1.5} />
            <span className="font-mono tabular-nums text-[11px]">{streak}</span>
          </span>
          <span className={`inline-flex items-center gap-1 ${krColor}`}>
            <Target size={14} strokeWidth={1.5} />
            <span className="font-mono tabular-nums text-[10px]">
              {krPct != null ? `${krPct}%` : '—'}
            </span>
          </span>
          <span className={`inline-flex items-center gap-1 ${coachColor}`}>
            <Clock size={14} strokeWidth={1.5} />
            <span className="font-mono tabular-nums text-[10px]">{coachLabel}</span>
          </span>
        </Link>
      </Tooltip>
    );
  }

  return (
    <Tooltip label={tooltip}>
      <Link
        to="/stats"
        className="group w-full flex items-center gap-1.5 px-3 py-2 hover:bg-linesoft transition-colors"
        data-testid="streak-widget"
        data-streak={streak}
        data-kr-pct={krPct ?? ''}
        data-coach-days={coachDays ?? ''}
        aria-label={tooltip}
      >
        <span className={`inline-flex items-center gap-1 ${streakColor}`}>
          <StreakIcon size={13} strokeWidth={1.5} />
          <span className="font-mono tabular-nums text-sm">{streak}</span>
        </span>
        <span className="font-mono text-mute text-[10px] select-none">·</span>
        <span className={`inline-flex items-center gap-1 ${krColor}`}>
          <Target size={13} strokeWidth={1.5} />
          <span className="font-mono tabular-nums text-sm">
            {krPct != null ? `${krPct}%` : '—'}
          </span>
        </span>
        <span className="font-mono text-mute text-[10px] select-none">·</span>
        <span className={`inline-flex items-center gap-1 ${coachColor}`}>
          <Clock size={13} strokeWidth={1.5} />
          <span className="font-mono tabular-nums text-sm">{coachLabel}</span>
        </span>
      </Link>
    </Tooltip>
  );
}
